import type { components } from "../api/schema";

type TradeResponse = components["schemas"]["TradeResponse"];
type FillAction = components["schemas"]["FillAction"];

export interface TradeAnalyticsKpis {
  realizedPnlAmount: string;
  realizedPnlCurrency: string;
  openingNotionalAmount: string | null;
  realizedReturnPct: string | null;
  holdDurationSeconds: number | null;
  rMultiple: string | null;
  plannedRiskAmount: string | null;
  plannedRiskCurrency: string | null;
}

export interface LegPnlSummary {
  legId: string;
  instrumentId: string;
  direction: "long" | "short";
  realizedPnlAmount: string;
  realizedPnlCurrency: string;
  openingNotionalAmount: string;
  fillCount: number;
  lotCount: number;
  remainingQty: string;
  targetQty: string;
}

function sumTradeOpeningNotional(trade: TradeResponse): number {
  let total = 0;
  for (const leg of trade.legs) {
    for (const lot of leg.lots) {
      total += Number(lot.opening_price_amount) * Number(lot.original_qty);
    }
  }
  return total;
}

function computeHoldSeconds(
  openedAt: string | null,
  closedAt: string | null,
): number | null {
  if (openedAt === null || closedAt === null) {
    return null;
  }
  const o = new Date(openedAt).getTime();
  const c = new Date(closedAt).getTime();
  if (Number.isNaN(o) || Number.isNaN(c)) {
    return null;
  }
  return Math.round((c - o) / 1000);
}

export function computeTradeAnalyticsKpis(
  trade: TradeResponse,
): TradeAnalyticsKpis {
  const realized = Number(trade.realized_pnl_amount);
  const openingNotional = sumTradeOpeningNotional(trade);
  const realizedReturnPct =
    openingNotional > 0
      ? ((realized / openingNotional) * 100).toFixed(2)
      : null;
  const planned =
    trade.planned_risk_amount !== null && trade.planned_risk_amount !== undefined
      ? Number(trade.planned_risk_amount)
      : 0;
  const rMultiple = planned > 0 ? (realized / planned).toFixed(2) : null;

  return {
    realizedPnlAmount: trade.realized_pnl_amount,
    realizedPnlCurrency: trade.realized_pnl_currency,
    openingNotionalAmount:
      openingNotional > 0 ? openingNotional.toFixed(2) : null,
    realizedReturnPct,
    holdDurationSeconds: computeHoldSeconds(trade.opened_at, trade.closed_at),
    rMultiple,
    plannedRiskAmount: trade.planned_risk_amount ?? null,
    plannedRiskCurrency: trade.planned_risk_currency ?? null,
  };
}

export function computeLegPnls(trade: TradeResponse): LegPnlSummary[] {
  return trade.legs.map((leg) => {
    let realized = 0;
    let opening = 0;
    for (const lot of leg.lots) {
      realized += Number(lot.realized_pnl_amount);
      opening += Number(lot.opening_price_amount) * Number(lot.original_qty);
    }
    return {
      legId: leg.leg_id,
      instrumentId: leg.instrument_id,
      direction: leg.direction,
      realizedPnlAmount: realized.toFixed(2),
      realizedPnlCurrency: trade.realized_pnl_currency,
      openingNotionalAmount: opening.toFixed(2),
      fillCount: leg.fills.length,
      lotCount: leg.lots.length,
      remainingQty: leg.open_qty,
      targetQty: leg.target_qty,
    };
  });
}

export function formatHoldDuration(seconds: number | null): string {
  if (seconds === null) {
    return "—";
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) {
    return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

export function isOpeningAction(action: FillAction): boolean {
  return action === "bto" || action === "sto";
}
